/**
 * WalkthroughProvider.js
 *
 * Global provider for the guided onboarding tour.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { WalkthroughStateContext, WalkthroughActionsContext, WALKTHROUGH_LS_KEY } from './WalkthroughContext';
import { TOUR_STEPS, STEP_ROUTES, TOTAL_STEPS } from './walkthroughSteps';
import WalkthroughTooltip from './WalkthroughTooltip';
import WelcomeModal from './WelcomeModal';
import { getWalkthroughStyles } from './WalkthroughStyles';

// Time to wait after a route change before activating the next step.
// Must be long enough for page layout to settle before Joyride measures targets.
const ROUTE_SETTLE_MS = 800;

const WalkthroughProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  // Ref tracking the current pathname — avoids stale closures in Joyride callbacks
  const locationRef = useRef(location.pathname);
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [joyrideKey, setJoyrideKey] = useState(0);

  // Guard ref — prevents re-entrant navigation callbacks
  const isNavigatingRef = useRef(false);
  const navTimerRef = useRef(null);
  // When set to true, the location-change effect will NOT auto-show the
  // Welcome Modal. This prevents a race condition where navigating to
  // /dashboard during startTour/resetTour re-triggers the modal while
  // Joyride is already being initialised.
  const suppressWelcomeModalRef = useRef(false);

  const isCompleted = () => localStorage.getItem(WALKTHROUGH_LS_KEY) === 'true';

  // Cleanup navigation timer on unmount
  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const isDashboard = location.pathname.startsWith('/dashboard');

    // If a tour start/reset triggered this navigation, lift the suppression
    // flag and do NOT show the Welcome Modal — the tour handles its own flow.
    if (suppressWelcomeModalRef.current) {
      suppressWelcomeModalRef.current = false;
      return;
    }

    // Show welcome modal automatically when user lands on dashboard for the first time
    if (isDashboard && !isCompleted()) {
      const t = setTimeout(() => setShowWelcomeModal(true), 300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const markCompleted = useCallback(() => {
    localStorage.setItem(WALKTHROUGH_LS_KEY, 'true');
  }, []);

  const normalizePath = (p) => p.replace(/\/$/, '') || '/';

  const startTour = useCallback(() => {
    // Cancel any pending nav timer from a previous start/reset
    if (navTimerRef.current) clearTimeout(navTimerRef.current);

    setIsRunning(false);
    setShowWelcomeModal(false);
    setStepIndex(0);
    setJoyrideKey((k) => k + 1);

    const firstRoute = STEP_ROUTES[0];
    const modalFadeOutDelay = 500;

    if (normalizePath(locationRef.current) !== normalizePath(firstRoute)) {
      suppressWelcomeModalRef.current = true;
      navigate(firstRoute);
      navTimerRef.current = setTimeout(() => setIsRunning(true), ROUTE_SETTLE_MS + modalFadeOutDelay);
    } else {
      navTimerRef.current = setTimeout(() => setIsRunning(true), modalFadeOutDelay);
    }
  }, [navigate]);

  const stopTour = useCallback(() => {
    setIsRunning(false);
  }, []);

  const skipTour = useCallback(() => {
    setIsRunning(false);
    setShowWelcomeModal(false);
    markCompleted();
  }, [markCompleted]);

  const completeTour = useCallback(() => {
    setIsRunning(false);
    markCompleted();
    toast.success('Tour completed! You\'re all set to explore the marketplace.');
  }, [markCompleted]);

  const resetTour = useCallback(() => {
    // Cancel any pending nav timer to avoid stale isRunning=true calls
    if (navTimerRef.current) clearTimeout(navTimerRef.current);

    localStorage.removeItem(WALKTHROUGH_LS_KEY);
    setIsRunning(false);
    setShowWelcomeModal(false);
    setStepIndex(0);
    setJoyrideKey((k) => k + 1);

    const firstRoute = STEP_ROUTES[0];
    if (normalizePath(locationRef.current) !== normalizePath(firstRoute)) {
      // Suppress the location effect so it doesn't re-show the Welcome Modal
      suppressWelcomeModalRef.current = true;
      navigate(firstRoute);
      navTimerRef.current = setTimeout(() => setIsRunning(true), ROUTE_SETTLE_MS);
    } else {
      navTimerRef.current = setTimeout(() => setIsRunning(true), ROUTE_SETTLE_MS);
    }
  }, [navigate]);

  const goToStep = useCallback(
    (targetIndex) => {
      if (targetIndex < 0 || targetIndex >= TOTAL_STEPS) return;
      const targetRoute = STEP_ROUTES[targetIndex];

      // Use ref instead of closure — always reads current pathname
      if (normalizePath(locationRef.current) !== normalizePath(targetRoute)) {
        // Clear any pending nav timer before starting a new one
        if (navTimerRef.current) clearTimeout(navTimerRef.current);

        isNavigatingRef.current = true;
        setIsRunning(false);
        navigate(targetRoute);

        navTimerRef.current = setTimeout(() => {
          setStepIndex(targetIndex);
          setJoyrideKey((k) => k + 1);
          setIsRunning(true);
          isNavigatingRef.current = false;
        }, ROUTE_SETTLE_MS);
      } else {
        // Same route: let the browser paint once before updating step index,
        // so Joyride re-measures the correct target position.
        requestAnimationFrame(() => {
          setStepIndex(targetIndex);
        });
      }
    },
    [navigate]
  );

  // Joyride event handler
  const handleJoyrideCallback = useCallback(
    (data) => {
      const { action, index, status, type } = data;

      if (isNavigatingRef.current) return;

      if (status === STATUS.FINISHED) {
        completeTour();
        return;
      }

      // Handle CLOSE/SKIP first — always return to prevent falling through to STEP_AFTER
      if (action === ACTIONS.CLOSE) {
        skipTour();
        return;
      }
      if (status === STATUS.SKIPPED) {
        skipTour();
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;

        if (nextIndex >= TOTAL_STEPS) {
          completeTour();
          return;
        }
        if (nextIndex < 0) return;

        goToStep(nextIndex);
      }
    },
    [completeTour, skipTour, goToStep]
  );

  const stateValue = useMemo(() => ({
    isRunning,
    stepIndex,
    isCompleted: isCompleted(),
    showWelcomeModal,
  }), [isRunning, stepIndex, showWelcomeModal]);

  const actionsValue = useMemo(() => ({
    startTour,
    stopTour,
    skipTour,
    completeTour,
    resetTour,
    goToStep,
    handleJoyrideCallback,
    setShowWelcomeModal,
  }), [startTour, stopTour, skipTour, completeTour, resetTour, goToStep, handleJoyrideCallback]);

  const walkthroughStyles = useMemo(() => getWalkthroughStyles(isDark), [isDark]);

  return (
    <WalkthroughActionsContext.Provider value={actionsValue}>
      <WalkthroughStateContext.Provider value={stateValue}>
        {children}

        {showWelcomeModal && (
          <WelcomeModal onStart={startTour} onSkip={skipTour} />
        )}

        <Joyride
          key={joyrideKey}
          steps={TOUR_STEPS}
          stepIndex={stepIndex}
          run={isRunning}
          continuous
          scrollToFirstStep
          scrollOffset={100}
          showProgress={false}
          showSkipButton={false}
          disableOverlayClose
          disableCloseOnEsc={false}
          spotlightClicks={TOUR_STEPS[stepIndex]?.spotlightClicks ?? false}
          onEvent={handleJoyrideCallback}
          tooltipComponent={WalkthroughTooltip}
          options={walkthroughStyles.options}
          styles={walkthroughStyles}
          floatingOptions={{
            disableAnimation: false,
            hideArrow: false,
          }}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Finish',
            next: 'Continue',
            open: 'Open',
            skip: 'Skip',
          }}
        />
      </WalkthroughStateContext.Provider>
    </WalkthroughActionsContext.Provider>
  );
};

export default WalkthroughProvider;